import { useEffect, useState } from "react";
import { cn } from "./ui";
import { api, apiErrorText } from "@/lib/api";
import { useAuth } from "./auth";
import { t, locale } from "@/lib/i18n";

/* Ответственный за клиента (компания или контакт) — один блок для карточки компании и панели контакта.
   Свободного клиента берёт любой («Это мой клиент»); переназначить или снять — менеджер и выше (выпадающий список).
   «История» — кто отвечал раньше (журнал действий company.owner / contact.owner). */

export type OwnerRef = { id: string; name: string } | null;
export type OwnerHistoryItem = { at: string; actor: string | null; from: string; to: string };

type UserRow = { id: string; name: string; email: string; status: string };

export function OwnerControl({ kind, id, owner, onChanged, compact }: { kind: "companies" | "contacts"; id: string; owner: OwnerRef; onChanged: (o: OwnerRef) => void; compact?: boolean }) {
  const { user } = useAuth();
  const staff = user?.role === "owner" || user?.role === "admin" || user?.role === "manager";
  const [users, setUsers] = useState<UserRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState<OwnerHistoryItem[] | null>(null);

  useEffect(() => {
    if (staff) api<UserRow[]>("/users").then((rows) => setUsers(rows.filter((r) => r.status !== "disabled")), () => setUsers([]));
  }, [staff]);

  useEffect(() => {
    if (!open) return;
    setHistory(null);
    api<{ items: OwnerHistoryItem[] }>(`/${kind}/${id}/owner-history`).then((r) => setHistory(r.items), () => setHistory([]));
  }, [open, kind, id, owner?.id]);

  const assign = async (userId: string | null) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const r = await api<{ owner: OwnerRef }>(`/${kind}/${id}/owner`, { method: "POST", json: { userId } });
      onChanged(r.owner);
    } catch (e) {
      setError(apiErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  const mine = Boolean(owner && user && owner.id === user.id);
  const viewer = user?.role === "viewer";

  return (
    <div className={cn("flex flex-col", compact ? "gap-1" : "gap-1.5")} data-testid="owner-control">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] font-semibold text-muted-foreground">{t("Ответственный")}:</span>
        {staff ? (
          <div className="relative">
            <select
              value={owner?.id ?? ""}
              disabled={busy}
              onChange={(e) => void assign(e.target.value || null)}
              aria-label={t("Ответственный")}
              className="h-7 cursor-pointer appearance-none rounded-btn border border-border bg-background pe-6 ps-2 text-[11.5px] font-medium text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="owner-select"
            >
              <option value="">{t("— без ответственного —")}</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email}
                </option>
              ))}
              {owner && !users.some((u) => u.id === owner.id) ? <option value={owner.id}>{owner.name}</option> : null}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute end-1 top-1/2 -translate-y-1/2 text-[12px] leading-none text-muted-foreground" aria-hidden />
          </div>
        ) : owner ? (
          <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-semibold", mine ? "bg-primary/10 text-primary" : "bg-muted text-foreground/80")}>
            <i className="ri-user-star-line text-[12px] leading-none" aria-hidden />
            {mine ? t("вы") : owner.name}
          </span>
        ) : viewer ? (
          <span className="text-[11.5px] text-muted-foreground">{t("не назначен")}</span>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => void assign(user?.id ?? null)}
            className="inline-flex h-7 cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn bg-primary px-2.5 text-[11.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:opacity-50"
            data-testid="owner-claim"
          >
            <i className="ri-hand-heart-line text-[12px] leading-none" aria-hidden />
            {t("Это мой клиент")}
          </button>
        )}
        <button type="button" onClick={() => setOpen((v) => !v)} className="cursor-pointer text-[11px] text-muted-foreground underline-offset-2 hover:text-primary hover:underline" aria-expanded={open}>
          {open ? t("скрыть историю") : t("история")}
        </button>
      </div>
      {error ? <p className="text-[11px] text-red-500">{error}</p> : null}
      {open ? (
        <ul className="flex flex-col gap-0.5 rounded-btn border border-border bg-background/60 px-2.5 py-1.5 text-[11px] text-muted-foreground">
          {history === null ? <li>{t("Загрузка…")}</li> : null}
          {history && history.length === 0 ? <li>{t("Ответственный ещё не менялся.")}</li> : null}
          {(history ?? []).map((h, i) => (
            <li key={i} className="flex flex-wrap gap-x-2">
              <span className="tabular-nums">{new Date(h.at).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" })}</span>
              <span className="text-foreground/80">
                {h.from || t("никто")} → {h.to || t("никто")}
              </span>
              <span>· {h.actor ?? t("система")}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
